// The opening-hours editor's week model (SEN-81) — pure, no React.
//
// Storage is Google Places' `opening_hours.periods`, unchanged: day 0 = Sunday,
// time "HHMM", a period per open span, an overnight span closing on the next
// day, and a lone open-ended period meaning open 24/7. That shape is read by
// monad.operating_zone (the one reader behind out_of_hours_consumption and
// time-part(operating_zone)); this module only translates it to and from a
// per-day model a person can edit, and checks the week the way that reader
// will interpret it. The server's codec still validates the shape — nothing
// here coerces a value the codec would refuse.
//
// Days are indexed by their STORED number (0 = Sunday). Display order is the
// ISO rotation declared once in display/projections.ts (ISO_WEEK).
import { ISO_WEEK, formatField } from '../display/projections'

/** One open span, as the editor holds it: "HH:MM" wall-clock times. A close at
 *  or before the open runs past midnight (22:00–02:00; 10:00–00:00 closes at
 *  midnight). */
export interface TimeRange {
  open: string
  close: string
}

export type DayHours =
  | { mode: 'closed' }
  | { mode: '24h' }
  | { mode: 'hours'; ranges: TimeRange[] }

/** Seven days, indexed by stored day number (0 = Sunday). */
export type Week = DayHours[]

interface Endpoint {
  day: number
  time: string
}
export interface Period {
  open: Endpoint
  close?: Endpoint
}

export interface WeekProblem {
  /** The stored day the problem belongs to; null for the week as a whole. */
  day: number | null
  message: string
}

const DAY_MINUTES = 1440
const WEEK_MINUTES = 7 * DAY_MINUTES
const HHMM_STORED = /^([01]\d|2[0-3])[0-5]\d$/
const HHMM_EDITED = /^([01]\d|2[0-3]):[0-5]\d$/

export const dayName = (day: number): string => formatField('day-of-week', day)

export const closedWeek = (): Week => Array.from({ length: 7 }, () => ({ mode: 'closed' as const }))

const toEdited = (stored: string) => `${stored.slice(0, 2)}:${stored.slice(2)}`
const toStored = (edited: string) => edited.replace(':', '')
const minutes = (edited: string) => Number(edited.slice(0, 2)) * 60 + Number(edited.slice(3))

function readEndpoint(ep: unknown): Endpoint | null {
  if (!ep || typeof ep !== 'object') return null
  const { day, time } = ep as { day?: unknown; time?: unknown }
  if (!Number.isInteger(day) || (day as number) < 0 || (day as number) > 6) return null
  if (typeof time !== 'string' || !HHMM_STORED.test(time)) return null
  return { day: day as number, time }
}

/** Stored value → editable week, or the reason this editor cannot show it.
 *  A shape the per-day model cannot hold (a span longer than a day) is
 *  refused by name — never reshaped silently on save. */
export function weekFromValue(value: unknown): { week: Week } | { unsupported: string } {
  if (value == null) return { week: closedWeek() }
  const periods = typeof value === 'object' ? (value as { periods?: unknown }).periods : undefined
  if (periods == null) return { week: closedWeek() }
  if (!Array.isArray(periods)) return { unsupported: 'The stored hours are not a list of periods' }

  const week = closedWeek()
  for (const p of periods) {
    const open = readEndpoint((p as Period | null)?.open)
    if (!open) return { unsupported: `Unreadable period ${JSON.stringify(p)}` }
    const rawClose = (p as Period).close
    if (rawClose == null) {
      // Open with no close: the reader treats the venue as open all week.
      return { week: Array.from({ length: 7 }, () => ({ mode: '24h' as const })) }
    }
    const close = readEndpoint(rawClose)
    if (!close) return { unsupported: `Unreadable period ${JSON.stringify(p)}` }

    const openMin = minutes(toEdited(open.time))
    const span = (((close.day - open.day + 7) % 7) * DAY_MINUTES + minutes(toEdited(close.time)) - openMin + WEEK_MINUTES) % WEEK_MINUTES
    const current = week[open.day]
    if (span === DAY_MINUTES && openMin === 0) {
      if (current.mode !== 'closed') return { unsupported: `${dayName(open.day)} has 24h and other hours` }
      week[open.day] = { mode: '24h' }
    } else if (span > 0 && span < DAY_MINUTES) {
      if (current.mode === '24h') return { unsupported: `${dayName(open.day)} has 24h and other hours` }
      const range = { open: toEdited(open.time), close: toEdited(close.time) }
      week[open.day] = { mode: 'hours', ranges: [...(current.mode === 'hours' ? current.ranges : []), range] }
    } else {
      return {
        unsupported: `${dayName(open.day)} ${toEdited(open.time)} → ${dayName(close.day)} ${toEdited(close.time)} spans more than a day`,
      }
    }
  }
  for (const d of week) {
    if (d.mode === 'hours') d.ranges.sort((a, b) => a.open.localeCompare(b.open))
  }
  return { week }
}

/** Editable week → the stored value, in Google's order (day 0 first, then by
 *  opening time). Seven 24h days are Google's own 24/7 shape: one open-ended
 *  period. The editor commits `{ periods }` only. */
export function valueFromWeek(week: Week): { periods: Period[] } {
  if (week.every((d) => d.mode === '24h')) return { periods: [{ open: { day: 0, time: '0000' } }] }
  const periods: Period[] = []
  week.forEach((d, day) => {
    if (d.mode === '24h') {
      periods.push({ open: { day, time: '0000' }, close: { day: (day + 1) % 7, time: '0000' } })
    } else if (d.mode === 'hours') {
      for (const r of [...d.ranges].sort((a, b) => a.open.localeCompare(b.open))) {
        const overnight = minutes(r.close) <= minutes(r.open)
        periods.push({
          open: { day, time: toStored(r.open) },
          close: { day: overnight ? (day + 1) % 7 : day, time: toStored(r.close) },
        })
      }
    }
  })
  return { periods }
}

type Span = { day: number; label: string; parts: Array<[number, number]> }

function spanOf(day: number, label: string, start: number, length: number): Span {
  const s = day * DAY_MINUTES + start
  const e = s + length
  // A span past Saturday midnight continues from Sunday 00:00 — the week wraps.
  const parts: Array<[number, number]> = e > WEEK_MINUTES ? [[s, WEEK_MINUTES], [0, e - WEEK_MINUTES]] : [[s, e]]
  return { day, label, parts }
}

const overlaps = (a: Span, b: Span) =>
  a.parts.some(([as, ae]) => b.parts.some(([bs, be]) => as < be && bs < ae))

/** Check a week as monad.operating_zone will read it: well-formed HH:MM times,
 *  no zero-length range, no two spans covering the same minute (an overnight
 *  range counts against the next day's hours). Problems come in display
 *  (Monday-first) order. */
export function validateWeek(week: Week): WeekProblem[] {
  const problems: WeekProblem[] = []
  const seen: Span[] = []
  for (const day of ISO_WEEK) {
    const d = week[day]
    const spans: Span[] = []
    if (d.mode === '24h') spans.push(spanOf(day, `${dayName(day)} 24h`, 0, DAY_MINUTES))
    if (d.mode === 'hours') {
      if (d.ranges.length === 0) {
        problems.push({ day, message: `${dayName(day)} needs a time range, or set it to Closed` })
        continue
      }
      for (const r of d.ranges) {
        if (!HHMM_EDITED.test(r.open) || !HHMM_EDITED.test(r.close)) {
          problems.push({ day, message: `${dayName(day)} needs an opening and a closing time (HH:MM)` })
          continue
        }
        if (r.open === r.close) {
          problems.push({ day, message: `${dayName(day)} opens and closes at ${r.open} — choose Open 24 hours instead` })
          continue
        }
        const length = (minutes(r.close) - minutes(r.open) + DAY_MINUTES) % DAY_MINUTES
        spans.push(spanOf(day, `${dayName(day)} ${r.open}–${r.close}`, minutes(r.open), length))
      }
    }
    for (const span of spans) {
      const clash = seen.find((other) => overlaps(span, other))
      if (clash) problems.push({ day, message: `${span.label} overlaps ${clash.label}` })
      seen.push(span)
    }
  }
  if (problems.length === 0 && week.every((d) => d.mode === 'closed')) {
    problems.push({ day: null, message: 'Every day is closed. Open at least one day, or clear the opening hours.' })
  }
  return problems
}

/** Copy one day's hours onto other days (each gets its own copy). */
export function copyDay(week: Week, from: number, to: readonly number[]): Week {
  const src = week[from]
  return week.map((d, day) =>
    to.includes(day) && day !== from
      ? src.mode === 'hours'
        ? { mode: 'hours', ranges: src.ranges.map((r) => ({ ...r })) }
        : { ...src }
      : d,
  )
}
